/**
 * Print settings are split between two sources since !2359:
 * remote, which is stored on the server side, and local, stored in local storage.
 * This service handles reading and writing of print settings in both locations.
 * This doesn't handle VueX state but rather serves as an abstraction for VueX actions
 * and is not meant to be called directly by consumers.
 */
import { merge } from 'lodash-es'

import * as Remote from './remote.service'
import * as Local from './local.service'
import { isEmpty } from './utils'
import type { PrintSettings } from '@/types/printing.model'
import type { User } from '@/types/models'

export const defaults = (): PrintSettings => merge({}, Remote.defaults(), Local.defaults())

export async function save(options: Partial<PrintSettings>, user: User): Promise<PrintSettings> {
  return merge({}, await Remote.save(options), Local.save(options, user))
}

/**
 * Gets all print settings merged from both locations and normalized.
 *
 * Before !2359, all print settings were stored in local storage, so upon resolving them,
 * _if the remote data is empty_ then we override settings in both locations to reflect
 * the new schema.
 * This is a temporary solution for migrating to the new schema and should be removed in the future.
 */
export async function getAll(user: User): Promise<PrintSettings> {
  const remotePrintSettings = await Remote.getAll()
  if (!isEmpty(remotePrintSettings)) {
    return merge({}, remotePrintSettings, Local.getAll(user))
  } else {
    const localPrintSettings = merge({}, defaults(), Local.getPartial(user))
    await save(localPrintSettings, user)
    return localPrintSettings
  }
}
